import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  IconButton,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import Edit_Fine from "./Edit_Fine";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const FineTable = () => {
  const toast = useToast();

  const [fineData, setFineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [fineIdToDelete, setFineIdToDelete] = useState(null); // For the fine to be deleted
  const { isOpen, onOpen, onClose } = useDisclosure(); // Modal hooks

  const schoolId = sessionStorage.getItem("school_id");
  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    fetchFineData();
  }, [schoolId]);

  const RefreshData = () => {
    fetchFineData();
  };

  const fetchFineData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}/admin/bus/fee/list_fee_structure.php?school_id=${schoolId}`
      );

      const data = await response.json();
      console.log("Fetched data:", data);

      if (data.success) {
        setFineData(data.data || []);
      } else {
        throw new Error(data.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(fineData.length / rowsPerPage);
  const currentData = fineData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/admin/bus/fee/delete_fee_structure.php`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
            school_id: schoolId,
            id: fineIdToDelete,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        toast({
          title: "Success",
          description: "Bus route deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setFineData((prevData) =>
          prevData.filter((fine) => fine.fine_id !== fineIdToDelete)
        );
        onClose();
        fetchFineData();

        // Close the modal after successful deletion
      } else {
        throw new Error(data.message || "Failed to delete fine");
      }
    } catch (err) {
      setError(err.message);
      onClose(); // Close the modal if there's an error
    }
  };

  if (loading) {
    return (
      <Box padding="4" textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  // if (error) {
  //   return (
  //     <Box padding="4">
  //       <Alert status="error">
  //         <AlertIcon />
  //         {error}
  //       </Alert>
  //     </Box>
  //   );
  // }

  if (fineData.length === 0) {
    return (
      <Box padding="4" textAlign="center">
        <Heading size="md">No fine data available.</Heading>
      </Box>
    );
  }

  return (
    <Box padding="0" margin="0 auto">
      <Flex justify="space-between" className="sb" p={3}>
        <Select
          width="120px"
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </Select>
      </Flex>
      <Box overflowX="auto" maxWidth="100%">
        <Table
          sx={{
            "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
            "th, td": {
              textAlign: "center",
            },
          }}
        >
          <Thead>
            <Tr>
              <Th>Fine Amount</Th>
              <Th>Fine Type</Th>
              <Th>Intervals</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentData.map((fine) => (
              <Tr key={fine.fine_id}>
                <Td>{fine.fixed_fine_amount}</Td>
                <Td>{fine.fine_type}</Td>
                <Td>
                  {Array.isArray(fine.fine_intervals) &&
                  fine.fine_intervals.length > 0
                    ? fine.fine_intervals.map((interval, idx) => (
                        <Box key={idx}>
                          <strong>Days:</strong> {interval.days},{" "}
                          <strong>Fine:</strong> {interval.fine_amount}
                        </Box>
                      ))
                    : "No Intervals"}
                </Td>
                <Td textAlign="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap="8px"
                  >
                    <Edit_Fine
                      fineId={fine.id}
                      userId={userId}
                      schoolId={schoolId}
                      RefreshData={RefreshData}
                    />
                    <IconButton
                      aria-label="Delete Fine"
                      icon={<DeleteIcon />}
                      onClick={() => {
                        setFineIdToDelete(fine.id); // Set the fine ID to delete
                        onOpen(); // Open the confirmation modal
                      }}
                      colorScheme="red"
                      size="sm"
                    />
                  </Box>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Flex justify="end" align="center" className="sb2" p={3}>
        <Button
          onClick={() => handlePageChange(currentPage - 1)}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>

        <Stack direction="row" spacing={2} mx={2}>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i}
              onClick={() => handlePageChange(i + 1)}
              colorScheme={currentPage === i + 1 ? "teal" : "gray"}
            >
              {i + 1}
            </Button>
          ))}
        </Stack>

        <Button
          onClick={() => handlePageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this fine? This action cannot be
            undone.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={handleDelete}>
              Delete
            </Button>
            <Button variant="ghost" onClick={onClose} ml={3}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FineTable;
